import { inject, Injectable } from '@angular/core';
import { ConvertAuthorizedAmountResponse, CurrencyExchangeRateControllerV2Service } from '@app/generated-api';
import moment, { Moment } from 'moment';
import { map, Observable, of } from 'rxjs';

export interface ConvertedAmounts {
    patientsCurrency: number | null;
    usd: number | null;
    exchangeRateDate: string | null;
}

/**
 * Formats a Date as a calendar date (yyyy-MM-dd) using its local date parts.
 * Unlike toISOString(), it does not shift the date to UTC, so the exchange rate is taken for the date the user picked.
 */
export function toCalendarDateString(date: Date): string {
    const year = date.getFullYear().toString().padStart(4, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export type ExchangeRateDateInput = Date | Moment | string | null | undefined;

const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
const dateWithoutOffsetPattern = /^(\d{4}-\d{2}-\d{2})T[\d:.]+$/;

/**
 * Converts the date picked by the user to a calendar date (yyyy-MM-dd) sent for the exchange rate lookup.
 * - Date and ISO strings with an offset/Z are read in local time (they come from local date pickers),
 * - UTC moments (e.g. date-only values parsed with moment.utc) keep their UTC calendar date,
 * - date-only and offset-less strings keep their date part.
 * Returns null when the value is not a valid date.
 */
export function toExchangeRateDate(value: ExchangeRateDateInput): string | null {
    if (value === null || value === undefined || value === '') return null;

    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : toCalendarDateString(value);
    }

    if (moment.isMoment(value)) {
        return value.isValid() ? value.format('YYYY-MM-DD') : null;
    }

    if (typeof value !== 'string') return null;

    const trimmed = value.trim();
    if (dateOnlyPattern.test(trimmed)) return trimmed;

    const withoutOffset = dateWithoutOffsetPattern.exec(trimmed);
    if (withoutOffset) return withoutOffset[1];

    const parsed = moment(trimmed, [moment.ISO_8601, 'DD-MMM-YYYY', 'DD/MMM/YYYY'], 'en', true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

@Injectable({ providedIn: 'root' })
export class CurrencyExchangeRateService {
    private readonly apiService = inject(CurrencyExchangeRateControllerV2Service);

    convertAmount(
        authorized: number,
        expenseCurrency: string,
        date: ExchangeRateDateInput,
        patientCurrency: string | null
    ): Observable<ConvertedAmounts> {
        const exchangeRateDate = toExchangeRateDate(date);
        if (!exchangeRateDate) {
            return of({ patientsCurrency: null, usd: null, exchangeRateDate: null });
        }

        return this.apiService
            .currencyExchangeRateControllerV2ConvertAuthorizedAmount({
                authorizedAmount: authorized,
                expenseCurrency,
                date: exchangeRateDate,
                patientCurrency: patientCurrency ?? '',
            })
            .pipe(
                map((res: ConvertAuthorizedAmountResponse) => ({
                    patientsCurrency: res.authorizedAmountPatientsCurrency ?? null,
                    usd: res.authorizedAmountUsd ?? null,
                    exchangeRateDate: res.exchangeRateDate ?? null,
                }))
            );
    }
}
